import type { OnInit, TemplateRef } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { OfferKind } from '@core/offers-types';

import { OfferSelectService } from '../offer-select.service';
import { OfferSelectDialogItem, SelectionListGroup } from '../offer-select.types';
import { OffersSelectionListComponent } from '../offers-selection-list/offers-selection-list.component';

@Component({
  selector: 'rt-offers-selection-list-container',
  template: `
    @if (!loading) {
      <rt-offers-selection-list
        [listData]="listData"
        [jobAssignments]="jobAssignments()"
        [talentPoolAssignments]="talentPoolAssignments()"
        [offerTemplate]="offerTemplate()"
        (selectionChanges)="selectionChanges.emit($event)"
      />
    }
  `,
  styleUrls: ['./offers-selection-list-container.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [OffersSelectionListComponent],
})
export class OffersSelectionListContainerComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly offerSelectService = inject(OfferSelectService);

  public readonly offerTemplate = input<TemplateRef<any>>(undefined!);
  public readonly jobAssignments = input<number[]>(undefined!);
  public readonly talentPoolAssignments = input<number[]>(undefined!);
  public readonly getArchivedOffers = input<boolean>(false);

  public selectionChanges = output<{
    jobIds: number[];
    talentPoolIds: number[];
  }>();
  public archivedOffersLoaded = output<OfferSelectDialogItem[]>();
  public listData = {
    [SelectionListGroup.Job]: <OfferSelectDialogItem[]>[],
    [SelectionListGroup.TalentPool]: <OfferSelectDialogItem[]>[],
  };
  public SelectionListGroup = SelectionListGroup;
  public loading: boolean = true;
  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.offerSelectService
      .getOffers()
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(offers => {
        this.listData = {
          [SelectionListGroup.Job]: offers.filter(offer => offer.kind === OfferKind.Job),
          [SelectionListGroup.TalentPool]: offers.filter(
            offer => offer.kind === OfferKind.TalentPool,
          ),
        };
        this.loading = false;
        this.cdr.detectChanges();
      });
    if (this.getArchivedOffers()) {
      this.loadArchivedOffers();
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private loadArchivedOffers(): void {
    this.offerSelectService
      .getArchivedOffers()
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(offers => this.archivedOffersLoaded.emit(offers));
  }
}
